import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
    environment,
    Comment,
    CreateComment,
    Paginated,
    PageMetadataWithCursor
} from '@shs-digi-care-monorepo/frontend-lib';
import { EFileEntity } from '@shs-digi-care-monorepo/interfaces';
import { AttachmentService, UploadFileInput } from './attachment.service';

/**
 * Service for managing IRO request comments via API.
 */
@Injectable({
    providedIn: 'root'
})
export class CommentService {
    protected requestEndpoint = 'iro-nam-requests';
    private readonly httpClient = inject(HttpClient);
    private readonly attachmentService = inject(AttachmentService);

    /**
     * Retrieves comments for a request with cursor-based pagination.
     * @param requestId The request ID
     * @param params Pagination params (cursor and pageSize)
     * @returns Observable of paginated comments
     */
    public getComments(
        requestId: string,
        params: { cursor?: string; pageSize?: number }
    ): Observable<Paginated<Comment, PageMetadataWithCursor>> {
        return this.httpClient.get<Paginated<Comment, PageMetadataWithCursor>>(
            `${environment.apiUrl}/${this.requestEndpoint}/${requestId}/comments`,
            {
                params: {
                    cursor: params.cursor ?? '',
                    limit: params.pageSize?.toString() ?? '10'
                }
            }
        );
    }

    /**
     * Creates a new comment on a request.
     * Handles attachment uploads before creating the comment if files are provided.
     * @param requestId The request ID
     * @param comment The comment data
     * @param attachments Optional files to attach to the comment
     * @returns Observable of the created comment
     */
    public createComment(requestId: string, comment: CreateComment, attachments?: File[]): Observable<Comment> {
        return this.uploadCommentAttachments(attachments, requestId).pipe(
            switchMap((fileNames) => {
                const body: CreateComment = { ...comment };
                if (fileNames.length > 0) {
                    body.files = fileNames;
                }
                return this.httpClient.post<Comment>(
                    `${environment.apiUrl}/${this.requestEndpoint}/${requestId}/comments`,
                    body
                );
            })
        );
    }

    /**
     * Deletes a comment from a request.
     * @param requestId The request ID
     * @param commentId The comment ID to delete
     * @returns Observable of void
     */
    public deleteComment(requestId: string, commentId: string): Observable<void> {
        return this.httpClient.delete<void>(
            `${environment.apiUrl}/${this.requestEndpoint}/${requestId}/comments/${commentId}`
        );
    }

    /**
     * Uploads attachments for a comment and returns the file names.
     * @param attachments The files to upload
     * @param requestId The request ID
     * @returns Observable of uploaded file names
     */
    private uploadCommentAttachments(attachments: File[] | undefined, requestId: string): Observable<string[]> {
        if (!attachments || attachments.length === 0) {
            return of([]);
        }

        const uploads = attachments.map((file) => {
            const input: UploadFileInput = {
                fileEntity: EFileEntity.Request,
                requestId,
                file,
                fileName: `comments/${file.name}`
            };
            return this.attachmentService.uploadFile(input);
        });

        return forkJoin(uploads).pipe(switchMap(() => of(attachments.map((file) => file.name))));
    }
}
